import type { Breadcrumb } from '@/types'
import { type Ref, ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'

interface DynamicRoute {
  id: number
  name: string
  title: string
  _children?: DynamicRoute[]
}

interface UseDynamicRouteOptions<T extends DynamicRoute> {
  level: number
  _children: Ref<T[]>
  onSlugMismatch?: () => void
}

export const useDynamicRoute = <T extends DynamicRoute>(
  options: UseDynamicRouteOptions<T>,
) => {
  const router = useRouter()

  const slugs = computed<string[]>(() => {
    return (
      (router.currentRoute.value.params.pathMatch as string[]) || []
    ).filter((item) => !!item)
  })

  const currentSlug = computed<string | undefined>(() => {
    return slugs.value.at(-1)
  })

  const matchedItems = ref<T[]>([])

  watch(
    () => [slugs.value, options._children.value],
    () => {
      if (!slugs.value.length || !options._children.value.length) {
        matchedItems.value = []
        return
      }

      const results: T[] = []
      let searchFrom: T[] = options._children.value

      for (const slug of slugs.value) {
        const match = searchFrom.find((item) => item.name === slug)
        if (!match) {
          if (typeof options.onSlugMismatch === 'function') {
            options.onSlugMismatch()
          }
          return
        }

        results.push(match)
        searchFrom = (match._children || []) as T[]
      }

      matchedItems.value = results
    },
    { deep: true, immediate: true },
  )

  const matchedItem = computed<T | undefined>(() => {
    return matchedItems.value.at(-1) as T | undefined
  })

  const breadcrumbs = computed<Breadcrumb[]>(() => {
    return matchedItems.value.reduce(
      (acc: Breadcrumb[], item: DynamicRoute) => {
        acc.push({
          path: [...acc.flatMap((item) => item.path), item.name].join('/'),
          title: item.title,
        })

        return acc
      },
      [] as Breadcrumb[],
    )
  })

  return {
    slugs,
    currentSlug,
    matchedItem,
    matchedItems,
    breadcrumbs,
  }
}
